// src/components/Hermes/Tasks/HermesTaskList.tsx
// ============================================
// HermesTaskList - Hermes 节奏任务列表
//
// 布局：单列网格，紧凑卡片
// 每张卡片包含：
//   - 任务图标 + 中文名 + Cron 时间（人类可读）+ 状态指示灯
//   - 上次执行时间 + 耗时 / 下次执行时间 / 执行次数 / 成功率
//   - 手动触发按钮 / 暂停 / 恢复 / 查看日志
// 点击卡片展开：显示最近 5 次执行记录
// ============================================

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, FileText, ChevronDown } from "lucide-react";
import {
  toTaskDisplayInfo,
  cronToHuman,
  formatDuration,
  formatTime,
  formatDateTime,
  TASK_ICONS,
  type TaskStatus,
} from "./types";
import type {
  ScheduledTask,
  TaskExecutionRecord,
} from "../../../runtime/SchedulerRuntime/types";

interface Props {
  tasks: ScheduledTask[];
  history: TaskExecutionRecord[];
  triggeringTaskIds: string[];
  onTrigger: (taskId: string) => void;
  onPause: (taskId: string) => void;
  onResume: (taskId: string) => void;
  onViewLog: (taskId?: string) => void;
}

// 状态 → 颜色
const STATUS_COLORS: Record<TaskStatus, string> = {
  idle: "#6B7280",
  running: "#3B82F6",
  success: "#34D399",
  failed: "#F87171",
  paused: "#FBBF24",
};

// 状态指示灯
function StatusLight({ status }: { status: TaskStatus }) {
  const color = STATUS_COLORS[status];
  if (status === "running") {
    // 蓝色脉冲
    return (
      <motion.span
        className="inline-block w-2 h-2 rounded-full"
        style={{ background: color, boxShadow: `0 0 8px ${color}` }}
        animate={{ scale: [1, 1.6, 1], opacity: [1, 0.5, 1] }}
        transition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut" }}
      />
    );
  }
  return (
    <span
      className="inline-block w-2 h-2 rounded-full"
      style={{
        background: color,
        boxShadow: status !== "idle" ? `0 0 6px ${color}80` : "none",
      }}
    />
  );
}

export default function HermesTaskList({
  tasks,
  history,
  triggeringTaskIds,
  onTrigger,
  onPause,
  onResume,
  onViewLog,
}: Props) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // 空状态
  if (tasks.length === 0) {
    return (
      <div className="text-center py-8 text-[11px] font-mono text-gray-500">
        暂无 Hermes 节奏任务
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-2">
      {tasks.map((task) => {
        const info = toTaskDisplayInfo(
          task,
          history,
          triggeringTaskIds.includes(task.id)
        );
        const icon = TASK_ICONS[task.id] || "⚙️";
        const expanded = expandedId === task.id;
        const recent = history
          .filter((h) => h.taskId === task.id)
          .slice(0, 5);
        const totalRuns = info.successCount + info.failCount;
        const successRate =
          totalRuns > 0
            ? Math.round((info.successCount / totalRuns) * 100)
            : 0;

        return (
          <div
            key={task.id}
            className="rounded-lg border border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04] transition-colors overflow-hidden"
          >
            {/* 卡片主体 */}
            <div
              className="p-2.5 cursor-pointer"
              onClick={() => setExpandedId(expanded ? null : task.id)}
            >
              {/* 第 1 行：图标 + 名称 + cron + 状态灯 */}
              <div className="flex items-center gap-2 mb-1.5">
                <span className="text-base leading-none">{icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] font-medium text-white truncate">
                    {info.name}
                  </div>
                  <div className="text-[10px] font-mono text-gray-500">
                    {cronToHuman(info.cron)}
                  </div>
                </div>
                <StatusLight status={info.status} />
                <ChevronDown
                  size={12}
                  className="text-gray-500 transition-transform"
                  style={{ transform: expanded ? "rotate(180deg)" : "none" }}
                />
              </div>

              {/* 第 2 行：上次 / 下次 / 次数 / 成功率 */}
              <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 text-[10px] font-mono text-gray-400 mb-2">
                <div className="flex items-center gap-1">
                  <span className="text-gray-600">上次</span>
                  <span className="text-gray-300">
                    {formatTime(info.lastRunAt)}
                  </span>
                  <span className="text-gray-600">
                    ({formatDuration(info.lastDuration)})
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-gray-600">下次</span>
                  <span className="text-gray-300">
                    {formatTime(info.nextRunAt)}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-gray-600">共</span>
                  <span className="text-gray-300">{info.runCount}</span>
                  <span className="text-gray-600">次</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-gray-600">成功率</span>
                  <span
                    style={{
                      color:
                        totalRuns === 0
                          ? "#6B7280"
                          : successRate >= 80
                          ? "#34D399"
                          : successRate >= 50
                          ? "#FBBF24"
                          : "#F87171",
                    }}
                  >
                    {totalRuns > 0 ? `${successRate}%` : "-"}
                  </span>
                </div>
              </div>

              {/* 第 3 行：操作按钮 */}
              <div className="flex items-center gap-1.5">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onTrigger(task.id);
                  }}
                  disabled={info.status === "running"}
                  className="flex items-center gap-1 px-2 py-1 rounded text-[10px] font-mono transition-colors disabled:opacity-50"
                  style={{
                    background: "rgba(59,130,246,0.12)",
                    color: "#60A5FA",
                    border: "1px solid rgba(59,130,246,0.3)",
                  }}
                  title="立即执行"
                >
                  <Play size={10} /> 触发
                </button>
                {info.enabled ? (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onPause(task.id);
                    }}
                    className="flex items-center gap-1 px-2 py-1 rounded text-[10px] font-mono text-amber-300 transition-colors"
                    style={{
                      background: "rgba(251,191,36,0.1)",
                      border: "1px solid rgba(251,191,36,0.25)",
                    }}
                    title="暂停"
                  >
                    <Pause size={10} /> 暂停
                  </button>
                ) : (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onResume(task.id);
                    }}
                    className="flex items-center gap-1 px-2 py-1 rounded text-[10px] font-mono text-emerald-300 transition-colors"
                    style={{
                      background: "rgba(52,211,153,0.1)",
                      border: "1px solid rgba(52,211,153,0.25)",
                    }}
                    title="恢复"
                  >
                    <Play size={10} /> 恢复
                  </button>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewLog(task.id);
                  }}
                  className="flex items-center gap-1 px-2 py-1 rounded text-[10px] font-mono text-gray-300 transition-colors ml-auto"
                  style={{
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.08)",
                  }}
                  title="查看日志"
                >
                  <FileText size={10} /> 日志
                </button>
              </div>
            </div>

            {/* 展开区：最近 5 次执行记录 */}
            <AnimatePresence initial={false}>
              {expanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="border-t border-white/5 overflow-hidden"
                >
                  <div className="p-2 space-y-1">
                    <div className="text-[9px] font-mono uppercase tracking-wider text-gray-600 mb-1">
                      最近执行（{recent.length}）
                    </div>
                    {recent.length === 0 ? (
                      <div className="text-[10px] font-mono text-gray-600">
                        暂无执行记录
                      </div>
                    ) : (
                      recent.map((h, i) => (
                        <div
                          key={i}
                          className="flex items-center gap-2 text-[10px] font-mono"
                        >
                          <span
                            style={{
                              color: h.success ? "#34D399" : "#F87171",
                            }}
                          >
                            {h.success ? "✓" : "✕"}
                          </span>
                          <span className="text-gray-400">
                            {formatDateTime(h.startedAt)}
                          </span>
                          <span className="text-gray-600">
                            {formatDuration(h.duration)}
                          </span>
                          {!h.success && h.error && (
                            <span
                              className="text-red-400/70 truncate flex-1"
                              title={h.error}
                            >
                              {h.error}
                            </span>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
}
